import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Brain, X, Sparkles, Send, Loader2 } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useFeature } from '@/lib/billing'
import { complete } from '@/lib/ai'
import { toast } from 'sonner'

export default function AIButton() {
  const { workspace } = useAuth()
  const hasAI = useFeature('ai')
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)

  if (!hasAI) return null

  const send = async () => {
    if (!input.trim() || loading) return
    const userMsg = input.trim()
    setInput('')
    setMessages(prev => [...prev, { role: 'user', content: userMsg }])
    setLoading(true)
    const { data, error } = await complete({
      messages: [...messages, { role: 'user', content: userMsg }],
      system: `You are a helpful AI assistant for ${workspace?.name || 'a business'} using Orkestra. Be concise — this is a quick-access chat widget. Keep responses under 150 words.`,
      max_tokens: 500,
    })
    if (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${error.message}` }])
      toast.error('AI request failed')
    } else {
      setMessages(prev => [...prev, { role: 'assistant', content: data!.text }])
    }
    setLoading(false)
  }

  return (
    <>
      {/* Floating button */}
      <motion.button
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen(o => !o)}
        style={{ position: 'fixed', bottom: 24, right: 24, width: 52, height: 52, borderRadius: 16, background: 'var(--logo-bg-gradient)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--ai-button-shadow)', zIndex: 500 }}>
        {open ? <X style={{ width: 20, height: 20, color: 'var(--accent-ink)' }} /> : <Brain style={{ width: 22, height: 22, color: 'var(--accent-ink)' }} />}
      </motion.button>

      {/* Chat popup */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            style={{ position: 'fixed', bottom: 88, right: 24, width: 360, height: 480, background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, zIndex: 500, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'var(--popup-shadow)' }}>

            {/* Header */}
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--accent-dim)', flexShrink: 0 }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Sparkles style={{ width: 14, height: 14, color: 'var(--accent-ink)' }} />
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>AI Assistant</p>
                <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>Powered by Claude</p>
              </div>
              <button onClick={() => setMessages([])}
                style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                Clear
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflow: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {messages.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 12px' }}>How can I help you?</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {['Draft a follow-up email', 'Summarize my open deals', 'How to handle an upset customer'].map(s => (
                      <button key={s} onClick={() => setInput(s)}
                        style={{ padding: '7px 10px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, fontSize: 12, color: 'var(--accent-text)', cursor: 'pointer', textAlign: 'left' }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '85%', padding: '8px 12px',
                    borderRadius: msg.role === 'user' ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                    background: msg.role === 'user' ? 'var(--accent-border)' : 'var(--border-subtle)',
                    border: `1px solid ${msg.role === 'user' ? 'var(--accent-border)' : 'var(--border-subtle)'}`,
                    fontSize: 12, color: msg.role === 'user' ? '#e9d5ff' /* allow-hex: decorative purple-tint ink on accent bubble */ : 'var(--ink-primary)',
                    lineHeight: 1.6, whiteSpace: 'pre-wrap',
                  }}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: '12px 12px 12px 3px', width: 'fit-content' }}>
                  <Loader2 style={{ width: 12, height: 12, color: 'var(--accent-text)', animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>Thinking...</span>
                </div>
              )}
            </div>

            {/* Input */}
            <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, flexShrink: 0 }}>
              <input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                placeholder="Ask anything..."
                style={{ flex: 1, padding: '8px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 12, color: 'var(--ink-primary)', outline: 'none' }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              <button onClick={send} disabled={loading || !input.trim()}
                style={{ width: 34, height: 34, borderRadius: 9, background: input.trim() ? 'var(--accent)' : 'var(--field-bg)', border: 'none', cursor: input.trim() ? 'pointer' : 'default', color: 'var(--accent-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Send style={{ width: 13, height: 13 }} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}